/* eslint-disable @nx/enforce-module-boundaries -- leaf modules; the barrel pulls Angular/Konva and breaks this lib's vitest */
import { buildPathD, parsePathD, type BezierAnchor } from '../../../canvas-engine/src/lib/svg/bezier-path-codec';

export type { BezierAnchor } from '../../../canvas-engine/src/lib/svg/bezier-path-codec';
/* eslint-enable @nx/enforce-module-boundaries */
import { buildPathSampler, type PathPoint, type PathSampler } from '@lite-workspace/photobook-model';

export type { PathPoint, PathSampler } from '@lite-workspace/photobook-model';

export type PathEditMode = 'range' | 'edit';

/** Structural subset of TextPathParams the overlay edits (source is carried through untouched). */
export interface TextPathWork {
  d: string;
  closed: boolean;
  startOffset?: number;
  pathRangeStart?: number;
  pathRangeEnd?: number;
  flip?: boolean;
  source?: string;
}

export interface PathEditSession {
  knots: BezierAnchor[];
  closed: boolean;
  sampler: PathSampler;
}

export const MIN_KNOTS_OPEN = 2;
export const MIN_KNOTS_CLOSED = 3;
const NEAREST_SCAN_SAMPLES = 200;

export function createPathEditSession(tp: Pick<TextPathWork, 'd' | 'closed'>): PathEditSession {
  const { knots, closed } = parsePathD(tp.d);
  return { knots, closed, sampler: buildPathSampler(tp.d, closed) };
}

export function sessionD(session: PathEditSession): string {
  return buildPathD(session.knots, session.closed);
}

export function refreshSessionSampler(session: PathEditSession): void {
  session.sampler = buildPathSampler(sessionD(session), session.closed);
}

/** Text container along the path in arc-length fractions; legacy startOffset is the fallback start. */
export function rangeFractions(tp: Pick<TextPathWork, 'startOffset' | 'pathRangeStart' | 'pathRangeEnd'>): {
  start: number;
  end: number;
} {
  const start = tp.pathRangeStart ?? tp.startOffset ?? 0;
  const end = tp.pathRangeEnd ?? 1;
  return { start: clamp01(start), end: Math.max(0, end) };
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

export function pointAtFraction(sampler: PathSampler, frac: number): PathPoint {
  return sampler.pointAtLength(clamp01(frac) * sampler.totalLength);
}

/** Brute-force nearest point on the curve (legacy text-path-editor parity — 200 samples). */
export function nearestFraction(sampler: PathSampler, pt: { x: number; y: number }): { frac: number; distance: number } {
  let best = { frac: 0, distance: Number.POSITIVE_INFINITY };
  for (let k = 0; k <= NEAREST_SCAN_SAMPLES; k++) {
    const frac = k / NEAREST_SCAN_SAMPLES;
    const p = sampler.pointAtLength(frac * sampler.totalLength);
    const d = Math.hypot(p.x - pt.x, p.y - pt.y);
    if (d < best.distance) best = { frac, distance: d };
  }

  return best;
}

/** Rigid anchor move: the anchor drags its two control handles with it (legacy parity). */
export function moveKnot(knots: readonly BezierAnchor[], index: number, dx: number, dy: number): BezierAnchor[] {
  return knots.map((k, i) =>
    i === index
      ? {
          in: [k.in[0] + dx, k.in[1] + dy] as [number, number],
          anchor: [k.anchor[0] + dx, k.anchor[1] + dy] as [number, number],
          out: [k.out[0] + dx, k.out[1] + dy] as [number, number],
        }
      : k,
  );
}

/** Independent control-handle move — no auto-mirror of the opposite handle (legacy parity). */
export function moveKnotHandle(
  knots: readonly BezierAnchor[],
  index: number,
  side: 'in' | 'out',
  pt: { x: number; y: number },
): BezierAnchor[] {
  return knots.map((k, i) => (i === index ? { ...k, [side]: [pt.x, pt.y] as [number, number] } : k));
}

interface CubicRef {
  from: number;
  to: number;
  p0: [number, number];
  c1: [number, number];
  c2: [number, number];
  p1: [number, number];
  length: number;
}

function cubicsOf(session: PathEditSession): CubicRef[] {
  const { knots, closed } = session;
  const out: CubicRef[] = [];
  const segCount = closed ? knots.length : knots.length - 1;
  for (let i = 0; i < segCount; i++) {
    const a = knots[i];
    const b = knots[(i + 1) % knots.length];
    const ref: CubicRef = { from: i, to: (i + 1) % knots.length, p0: a.anchor, c1: a.out, c2: b.in, p1: b.anchor, length: 0 };
    let prev: [number, number] = ref.p0;
    for (let k = 1; k <= 32; k++) {
      const p = cubicPoint(ref, k / 32);
      ref.length += Math.hypot(p[0] - prev[0], p[1] - prev[1]);
      prev = p;
    }

    out.push(ref);
  }

  return out;
}

function cubicPoint(c: CubicRef, t: number): [number, number] {
  const omt = 1 - t;
  return [
    omt ** 3 * c.p0[0] + 3 * omt ** 2 * t * c.c1[0] + 3 * omt * t ** 2 * c.c2[0] + t ** 3 * c.p1[0],
    omt ** 3 * c.p0[1] + 3 * omt ** 2 * t * c.c1[1] + 3 * omt * t ** 2 * c.c2[1] + t ** 3 * c.p1[1],
  ];
}

/**
 * Insert a knot at the given arc-length fraction via a de Casteljau split — the curve SHAPE is preserved
 * exactly; only the knot list grows. Returns the new knot list and the inserted index.
 */
export function insertKnotAtFraction(session: PathEditSession, frac: number): { knots: BezierAnchor[]; index: number } {
  const cubics = cubicsOf(session);
  const total = cubics.reduce((s, c) => s + c.length, 0);
  let target = clamp01(frac) * total;
  let cubic = cubics[cubics.length - 1];
  for (const c of cubics) {
    if (target <= c.length || c === cubics[cubics.length - 1]) {
      cubic = c;
      break;
    }

    target -= c.length;
  }

  const t = cubic.length > 0 ? Math.min(1, Math.max(0, target / cubic.length)) : 0.5;
  const lerp = (a: [number, number], b: [number, number], tt: number): [number, number] => [
    a[0] + (b[0] - a[0]) * tt,
    a[1] + (b[1] - a[1]) * tt,
  ];
  const q0 = lerp(cubic.p0, cubic.c1, t);
  const q1 = lerp(cubic.c1, cubic.c2, t);
  const q2 = lerp(cubic.c2, cubic.p1, t);
  const r0 = lerp(q0, q1, t);
  const r1 = lerp(q1, q2, t);
  const split = lerp(r0, r1, t);

  const knots = session.knots.map(k => ({ ...k }));
  knots[cubic.from] = { ...knots[cubic.from], out: q0 };
  knots[cubic.to] = { ...knots[cubic.to], in: q2 };
  const inserted: BezierAnchor = { in: r0, anchor: split, out: r1 };
  const index = cubic.from + 1;
  knots.splice(index, 0, inserted);
  return { knots, index };
}

export function canRemoveKnot(knots: readonly BezierAnchor[], closed: boolean): boolean {
  return knots.length > (closed ? MIN_KNOTS_CLOSED : MIN_KNOTS_OPEN);
}

/** Remove a knot; null when the minimum (2 open / 3 closed) would be violated. */
export function removeKnot(knots: readonly BezierAnchor[], closed: boolean, index: number): BezierAnchor[] | null {
  if (!canRemoveKnot(knots, closed)) return null;
  if (index < 0 || index >= knots.length) return null;
  const next = knots.filter((_, i) => i !== index);
  return next;
}

/**
 * Shift both range edges by the same arc-length delta: whole-text drag along the curve.
 * Open path clamps at the ends (the span never shrinks); closed path wraps modulo 1.
 */
export function shiftRange(start: number, end: number, delta: number, closed: boolean): { start: number; end: number } {
  if (closed) {
    const wrap = (v: number): number => ((v % 1) + 1) % 1;
    return { start: wrap(start + delta), end: wrap(end + delta) };
  }

  const span = Math.min(1, Math.max(0, end - start));
  const s = Math.min(1 - span, Math.max(0, start + delta));
  return { start: s, end: s + span };
}
